import { AudioRecorder } from 'components/audio_recorder/audio_recorder'
import { AttachFilesToolbarButton } from 'components/topics/composer/toolbars/attach_files_toolbar_button'
import { FormattingToolbar } from 'components/topics/composer/toolbars/formatting_toolbar'
import { RecordingToolbar } from 'components/topics/composer/toolbars/recording_toolbar'
import { ToolbarButton } from 'components/topics/composer/toolbars/toolbar_button'
import { TrixEmoji } from 'components/trix/trix_emoji'
import { useMediaQuery } from 'hooks/use_media_query'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { mergeRefs } from 'react-merge-refs'

import { MEDIA_QUERIES, css } from 'design'
import { Button } from 'primitives/button'
import { Stack } from 'primitives/containers'
import { cn } from 'util/cn'

import {
  CloseToggleIcon,
  ComposerHeadingsIcon,
  ComposerNoteIcon,
  ComposerSubmitIcon,
  MenuDeleteIcon,
} from '../../../images'
import { isLiveChatApp } from '../../../reduxes/slices/live_chat_app/selectors'
import { useSelectorDelegateStore } from '../../../reduxes/stores/root_store/hooks'
import {
  ResponsiveTrixFormProps,
  TRIX_TOOLBAR_MINIMUM_WIDTH,
  TRIX_TOOLBAR_PADDING,
} from '../trix_form'

const composerFormStyles = css({
  display: 'flex',
  width: '100%',
  maxHeight: '50vh',
  justifyContent: 'flex-end',
  overflow: 'hidden',

  '#trix-editor-top-level': {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
  },

  variants: {
    variant: {
      default: {
        'trix-toolbar': {
          borderTop: '1px solid transparent',
          position: 'absolute',
          right: 0,
          bottom: '0',
          zIndex: 2,
        },
        '#trix-editor-top-level': {
          paddingRight: 'var(--trix-composer-toolbar-width)',
        },
      },
      split: {
        flexDirection: 'column',
        'trix-toolbar': {
          borderTop: '1px solid $grey100',
          width: '100%',
        },
        '#trix-editor-top-level': {
          padding: '$3 $4',
          minHeight: 'unset',
        },
      },
    },
    activeType: {
      message: {},
      note: {},
    },
  },

  compoundVariants: [
    {
      variant: 'split',
      activeType: 'note',
      css: {
        'trix-toolbar': {
          borderTop: '1px solid $yellow100',
        },
      },
    },
  ],

  defaultVariants: {
    variant: 'default',
  },
})

const trixToolbarStyles = css({
  width: '100% !important',
  alignItems: 'flex-start !important',
  padding: '0 $4',
  mixBlendMode: 'multiply',
  justifyContent: 'flex-end',

  '.topic__theme--dark &': {
    mixBlendMode: 'normal',
  },

  variants: {
    variant: {
      default: {},
      split: {
        width: '100%',
        justifyContent: 'space-between',
      },
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const shiftEnterButtonStyles = css({
  padding: 0,
  minHeight: 'auto',
  fontSize: '$0',
  borderRadius: '$1',
  color: '$grey600',

  '&:hover': {
    opacity: 1,
  },

  variants: {
    active: {
      true: {
        opacity: 1,
      },
      false: {
        opacity: 0.75,
      },
    },
  },

  defaultVariants: {
    active: 'false',
  },
})

export const DesktopTrixForm = React.forwardRef<
  HTMLFormElement,
  ResponsiveTrixFormProps
>((props, ref) => {
  const {
    id,
    passUpFocusAtStart,
    onActiveTypeChanged,
    activeType = 'message',
    focusAtStart,
    handleSubmit,
    handleKeyDown,
    isRecording,
    isSubmitDisabled,
    children,
    editor,
    setIsRecording,
    onCancel,
    hasAudioDevices,
    handleEnterToSend,
    enterToSend,
    variant,
  } = props

  const formRef = useRef<HTMLFormElement>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const [toolbarWidth, setToolbarWidth] = useState<any>({
    '--trix-composer-toolbar-width': 0,
  })
  const [isFormatting, setIsFormatting] = useState<boolean>(false)
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  useEffect(() => {
    if (focusAtStart && passUpFocusAtStart) {
      passUpFocusAtStart(focusAtStart)
    }
  }, [focusAtStart, passUpFocusAtStart])

  useLayoutEffect(() => {
    const toolbar = toolbarRef.current
    if (!toolbar) {
      return
    }

    const padding =
      toolbar.getBoundingClientRect()?.width + TRIX_TOOLBAR_PADDING || 0

    setToolbarWidth({
      '--trix-composer-toolbar-width': `${
        padding > TRIX_TOOLBAR_MINIMUM_WIDTH
          ? padding
          : TRIX_TOOLBAR_MINIMUM_WIDTH
      }px`,
    })
  }, [])

  useEffect(() => {
    const form = formRef.current
    if (!form) {
      return
    }

    const onSubmit = (e: any) => {
      e.preventDefault()
      e.stopPropagation()
      handleSubmit(e)
    }

    form.addEventListener('submit', onSubmit)

    return () => {
      form.removeEventListener('submit', onSubmit)
    }
  }, [handleSubmit])

  const xlBreakpoint = useMediaQuery(MEDIA_QUERIES.xl)

  return (
    <form
      key={`trix-form${id}`}
      ref={mergeRefs([ref, formRef])}
      onKeyDown={handleKeyDown}
      data-cy="composer-trix"
      className={composerFormStyles({
        variant:
          variant === 'split' ||
          isFormatting ||
          isRecording ||
          onCancel ||
          !xlBreakpoint
            ? 'split'
            : 'default',
        activeType,
      })}
      style={toolbarWidth}
      data-testid="composer-form__desktop"
    >
      {children}

      <trix-toolbar id="my_toolbar" data-testid="composer-toolbar__desktop">
        <Stack
          direction="row"
          className={cn(
            trixToolbarStyles({
              variant:
                isFormatting || isRecording || onCancel ? 'split' : 'default',
            }),
          )}
        >
          {/* Element to help align different toolbars */}
          {!isFormatting && !isRecording && onCancel && <span />}

          {isFormatting && <FormattingToolbar activeType={activeType} />}
          {isRecording && <RecordingToolbar />}
          <Stack
            gap="0"
            css={{
              width: 'auto',
              alignItems: 'flex-end',
              position: 'relative',
            }}
          >
            <Stack direction="row" gap="1" ref={toolbarRef as any}>
              {setIsFormatting && (
                <ToolbarButton
                  onClick={() => {
                    setIsFormatting((f: boolean) => !f)
                    if (setIsRecording) {
                      setIsRecording(false)
                    }
                  }}
                  toggled={isFormatting}
                  activeType={activeType}
                  title={isFormatting ? 'Stop formatting' : 'Apply formatting'}
                >
                  <ComposerHeadingsIcon />
                </ToolbarButton>
              )}

              <TrixEmoji
                editor={editor}
                activeType={activeType ? activeType : 'message'}
              />

              {!liveChatApp && onActiveTypeChanged && (
                <ToolbarButton
                  onClick={onActiveTypeChanged}
                  toggled={activeType === 'note'}
                  disabled={!onActiveTypeChanged}
                  className="composer-toolbar__notetoggle"
                  activeType={activeType}
                  title={
                    activeType === 'message'
                      ? 'Write a Note'
                      : 'Write a Message'
                  }
                >
                  <ComposerNoteIcon />
                </ToolbarButton>
              )}

              <AttachFilesToolbarButton activeType={activeType} />

              {hasAudioDevices && !onCancel && (
                <AudioRecorder
                  editor={editor}
                  isRecording={isRecording}
                  activeType={activeType}
                  setIsRecording={() => {
                    setIsRecording((f: boolean) => !f)
                    if (setIsFormatting) {
                      setIsFormatting(false)
                    }
                  }}
                />
              )}

              {onCancel && (
                <ToolbarButton
                  onClick={onCancel}
                  variant="danger"
                  className="composer-toolbar__cancel"
                  activeType={activeType}
                  title="Stop Editing"
                >
                  <MenuDeleteIcon />
                </ToolbarButton>
              )}

              <ToolbarButton
                className="composer-toolbar__submit"
                type="submit"
                variant="primary"
                activeType={activeType}
                disabled={isSubmitDisabled}
                size="small"
                title="Send Message"
                aria-label={'Send Message'}
              >
                {onCancel ? <CloseToggleIcon /> : <ComposerSubmitIcon />}
              </ToolbarButton>
            </Stack>

            {!onCancel && (
              <Button
                variant="tertiary"
                size="small"
                className={cn(
                  shiftEnterButtonStyles({ active: !isSubmitDisabled }),
                  'send-button-mode-toggle',
                )}
                onClick={handleEnterToSend}
                title={enterToSend ? 'Enter to send' : 'Shift-enter to send'}
              >
                {`${enterToSend ? 'enter' : 'shift-enter'} to send`}
              </Button>
            )}
          </Stack>
        </Stack>
      </trix-toolbar>
    </form>
  )
})

DesktopTrixForm.displayName = 'DesktopTrixForm'
